import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ProjectHeader = ({ project, onSave, onExport, onPublish }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState(project?.title);

  const handleSave = () => {
    onSave({ ...project, title: editedTitle });
    setIsEditing(false);
  };

  const getStatusColor = (status) => {
    const colors = {
      'draft': 'bg-warning/10 text-warning border-warning/20',
      'in-progress': 'bg-primary/10 text-primary border-primary/20',
      'completed': 'bg-success/10 text-success border-success/20',
      'published': 'bg-accent/10 text-accent border-accent/20'
    };
    return colors?.[status] || colors?.draft;
  };

  const getStatusIcon = (status) => {
    const icons = {
      'draft': 'FileEdit',
      'in-progress': 'Clock',
      'completed': 'CheckCircle',
      'published': 'Send'
    };
    return icons?.[status] || 'File';
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-sm p-6">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div className="flex-1 min-w-0">
          {isEditing ? (
            <div className="flex items-center gap-2 mb-3">
              <input
                type="text"
                value={editedTitle}
                onChange={(e) => setEditedTitle(e?.target?.value)}
                className="flex-1 px-3 py-2 bg-background border border-border rounded-md text-lg font-heading font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                autoFocus
              />
              <Button
                variant="default"
                size="sm"
                iconName="Check"
                onClick={handleSave}
              >
                Save
              </Button>
              <Button
                variant="ghost"
                size="sm"
                iconName="X"
                onClick={() => {
                  setEditedTitle(project?.title);
                  setIsEditing(false);
                }}
              />
            </div>
          ) : (
            <div className="flex items-center gap-2 mb-3">
              <h1 className="text-2xl lg:text-3xl font-heading font-bold text-foreground truncate">
                {project?.title}
              </h1>
              <button
                onClick={() => setIsEditing(true)}
                className="p-1.5 hover:bg-muted rounded-md transition-colors duration-200"
                aria-label="Edit title"
              >
                <Icon name="Edit2" size={18} className="text-muted-foreground" />
              </button>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <Icon name="BookOpen" size={16} />
              <span>{project?.genre}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Icon name="Users" size={16} />
              <span>{project?.targetAudience}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Icon name="Calendar" size={16} />
              <span>Created {project?.createdDate}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Icon name="Clock" size={16} />
              <span>Modified {project?.lastModified}</span>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row lg:flex-col gap-3 lg:items-end">
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md border ${getStatusColor(project?.status)}`}>
            <Icon name={getStatusIcon(project?.status)} size={16} />
            <span className="text-sm font-medium capitalize">{project?.status?.replace('-', ' ')}</span>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              iconName="Download"
              iconPosition="left"
              onClick={onExport}
            >
              Export
            </Button>
            {project?.status === 'completed' && (
              <Button
                variant="default"
                size="sm"
                iconName="Send"
                iconPosition="left"
                onClick={onPublish}
              >
                Publish
              </Button>
            )}
          </div>
        </div>
      </div>
      <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-muted/30 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="FileText" size={16} className="text-primary" />
            <span className="text-xs text-muted-foreground">Word Count</span>
          </div>
          <p className="text-lg font-heading font-semibold text-foreground">
            {project?.wordCount?.toLocaleString()}
          </p>
        </div>

        <div className="bg-muted/30 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="List" size={16} className="text-secondary" />
            <span className="text-xs text-muted-foreground">Chapters</span>
          </div>
          <p className="text-lg font-heading font-semibold text-foreground">
            {project?.chapterCount}
          </p>
        </div>

        <div className="bg-muted/30 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="TrendingUp" size={16} className="text-success" />
            <span className="text-xs text-muted-foreground">Progress</span>
          </div>
          <p className="text-lg font-heading font-semibold text-foreground">
            {project?.progress}%
          </p>
        </div>

        <div className="bg-muted/30 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="Zap" size={16} className="text-accent" />
            <span className="text-xs text-muted-foreground">AI Credits</span>
          </div>
          <p className="text-lg font-heading font-semibold text-foreground">
            {project?.creditsUsed}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProjectHeader;